import { PARENT_PROFESSION_SLUG } from 'constants/consts';
import { FOOTPRINT_TRACKING_ENDPOINT } from 'constants/footPrintTracking';
import { SORT_OPTION_VALUES } from 'constants/index';

import * as API from 'utils/API';
import { getOption } from 'utils/globals/app';
import { deleteQuery, getQuery, postQuery, updateQuery } from 'utils/globals/queries';
import { getLanguage } from 'utils/queriesUtil';
import { useMutation } from 'utils/react-query';

import { ENTITY_TYPES } from 'operabase-router/constants';

const {
  create,
  getWholeList,
  getOne,
  updateOne,
  getList,
  getProductsList,
  createOrganizationContacts,
  getCurrentUserRequestsAccess,
} = API;

const ENDPOINTS = {
  STATIC: {
    USERS_SUBSCRIPTIONS: 'users/subscriptions',
    PROFILES: 'profiles',
    PROFILE_ACCESS_REQUESTS: 'profiles/profileaccessrequests',
    PROFILES_ENDPOINT: 'profiles',
    COUNTRIES_ENDPOINT: 'countries',
    LANGUAGES_LIST: 'languages',
    PROFILE_TYPE_PREFERENCES: 'profiles/types',
    AGENCIES: 'agencies',
    ACCOUNT_CREATE: 'users',
    USER_REFERRALS: 'users/giftTokens',
    USER_REFERRALS_STATUS: 'users/referralStatus',
    ORGANIZATIONS: 'organizations',
    MUSICAL_WORK_TYPES_ENDPOINT: 'works/types',
    PERFORMANCES_TYPES: 'performances/types',
    PROFILE_ACCESSES: 'profiles/profileaccesses',
    ORGANIZATIONS_DEPARTMENTS: 'organizations/departments',
    PROFESSIONS: 'professions',
    PREFERENCES: 'preferences',
    WORKS_TYPES: 'works/types',
  },
  DYNAMIC: {
    USERS: ({ id }) => `users${id ? `/${id}` : ''}`,
    USER_PERMISSIONS: ({ id }) => `users/${id}/permissions`,
    USER_SUBSCRIPTIONS: ({ id }) => `users/${id}/subscriptions`,
    USER_PAYMENT_METHODS: ({ id }) => `users/${id}/paymentmethods`,
    USER_ACTIVE_PROFILE: ({ id }) => `users/${id}/settings/active_profile_id`,
    PROFILE_PERMISSIONS: ({ id }) => `profiles/${id}/permissions`,
    REGISTRATION: userId => `users/${userId}/registrations`,
    PROFILE_PREFERENCES: profileId => `profiles/${profileId}/preferences`,
    PROFILE: profileId => `profiles/${profileId}`,
    USER_PERFORMACES: userId => `users/${userId}/performances`,
    IDENTITY_VERIFICATION: userId => `users/${userId}/kyc`,
    SET_PROFILE_ACCESS_REQUESTS: profileId => `profiles/${profileId}/profileaccessrequests`,
    DELETE_PROFILE_ACCESSES: profileAccessId => `profiles/profileaccesses/${profileAccessId}`,
    GIFT_SUBSCRIPTIONS: userId => `users/${userId}/referralStatus`,
    ORGANIZATION_PERFORMANCE_TYPES: orgId => `organizations/${orgId}/performanceTypes`,
    ORGANIZATION_WORK_TYPES: orgId => `organizations/${orgId}/workTypes`,
    ORGANIZATION_STAGES: orgId => `organizations/${orgId}/stages`,
    CREATE_ADDITIONAL_EMAIL: userId => `users/${userId}/emailRequests`,
    VALIDATE_ADDITIONAL_EMAIL: userId => `users/${userId}/emailRequests/verifyWithOtp`,
  },
};

const defaultConfig = {
  retry: false,
  retryOnMount: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  staleTime: 10 * 60 * 1000,
};

const selectConfig = {
  select: response => ({
    ...response,
    data: response?.data?.map(option =>
      getOption(option, 'id', 'name', { id: option?.id, slug: option?.slug, name: option?.name }),
    ),
  }),
};

const fetchUserDetails = async ({ id }, cookies) => {
  if (!id) {
    return null;
  }

  return getOne(ENDPOINTS.DYNAMIC.USERS({}), id, null, cookies).then(response => response?.data);
};

const fetchUserPermissions = async ({ id }, cookies) => {
  if (!id) {
    return null;
  }

  return getList(ENDPOINTS.DYNAMIC.USER_PERMISSIONS({ id }), {}, cookies).then(response => response?.data);
};

const fetchOwnedProfiles = async ({ id }, cookies) => {
  const queryParams = {
    limit: 100,
    validation_status: 'pending,processing,approved,rejected,approved-hidden',
    access_user_id: id,
  };

  return getList(ENDPOINTS.STATIC.PROFILES, { queryParams }, cookies).then(response => response?.data?.data);
};

const fetchProfileAccessRequests = async ({ id }, cookies) => {
  const queryParams = {
    limit: 100,
    page: 1,
    user_id: id,
  };

  return getList(ENDPOINTS.STATIC.PROFILE_ACCESS_REQUESTS, { queryParams }, cookies).then(
    response => response?.data?.data,
  );
};

const fetchProfilesWithPendingAccess = async ({ ids }, cookies) => {
  const queryParams = {
    limit: 100,
    validation_status: 'pending,processing,approved,rejected,approved-hidden',
    id: ids,
  };

  return getList(ENDPOINTS.STATIC.PROFILES, { queryParams }, cookies).then(response => response?.data?.data);
};

const fetchTargetProfileSubscriptions = async ({ id, entityType }, cookies) => {
  let filterKey = 'profile_id';

  if (entityType === ENTITY_TYPES.AGENCY) {
    filterKey = 'agency_id';
  } else if (entityType === ENTITY_TYPES.ORGANIZATION) {
    filterKey = 'organization_id';
  }

  if (!id) {
    return null;
  }

  const queryParams = {
    [filterKey]: id,
    limit: 100,
  };

  return getList(ENDPOINTS.STATIC.USERS_SUBSCRIPTIONS, { queryParams }, cookies).then(response => response?.data);
};

const fetchUserProfiles = async ({ id }, cookies) => {
  const [profiles, profileAccessRequests] = await Promise.allSettled([
    fetchOwnedProfiles({ id }, cookies),
    fetchProfileAccessRequests({ id }, cookies),
  ]).then(results => results.map(result => result.value));

  let profilesWithPendingAccess = [];
  const accessPendingProfileIds =
    profileAccessRequests
      ?.filter(p => p?.status === 'pending' && p?.profile?.id && !p?.organization_id && !p?.agency_id)
      ?.map(p => p?.profile?.id) || [];

  const accessPendingOrganizationIds =
    profileAccessRequests
      ?.filter(p => p?.status === 'pending' && p?.organization?.id && !p?.agency_id)
      ?.map(p => p?.organization?.id) || [];

  const accessPendingAgencyIds =
    profileAccessRequests
      ?.filter(p => p?.status === 'pending' && p?.agency?.id && !p?.organization_id)
      ?.map(p => p?.agency?.id) || [];

  if (accessPendingProfileIds?.length > 0) {
    profilesWithPendingAccess = await fetchProfilesWithPendingAccess({ ids: accessPendingProfileIds }, cookies);
  }

  return {
    data: [...(profiles || []), ...(profilesWithPendingAccess || [])],
    accessPendingProfileIds,
    accessPendingOrganizationIds,
    accessPendingAgencyIds,
  };
};

export const fetchUserSubscriptions = async ({ id }, cookies) => {
  if (!id) {
    return null;
  }

  return getList(ENDPOINTS.DYNAMIC.USER_SUBSCRIPTIONS({ id }), { queryParams: { limit: 100 } }, cookies).then(
    response => response?.data,
  );
};

const fetchUserPaymentMethods = async ({ id }, cookies) => {
  if (!id) {
    return null;
  }

  return getList(ENDPOINTS.DYNAMIC.USER_PAYMENT_METHODS({ id }), {}, cookies).then(response => response?.data);
};

const fetchPreferences = async ({ limit = 50, page = 1, filters }, cookies) => {
  const params = {
    queryParams: filters,
    pagination: {
      limit,
      page,
    },
  };

  return getList(ENDPOINTS.STATIC.PREFERENCES, params, cookies).then(response => response?.data);
};

const fetchWorksTypes = async ({ limit = 50, page = 1, filters }, cookies) => {
  const params = {
    queryParams: { ...filters, validation_status: 'pending,approved', page: 1, limit: 9 },
    pagination: {
      limit,
      page,
    },
  };

  return getList(ENDPOINTS.STATIC.WORKS_TYPES, params, cookies).then(response => response?.data);
};

const fetchFingerprintDevices = async ({ limit = 50, page = 1, filters }, cookies) => {
  const params = {
    queryParams: filters,
    pagination: {
      limit,
      page,
    },
  };

  return getList(`${FOOTPRINT_TRACKING_ENDPOINT.getDevices}`, params, cookies).then(response => response?.data);
};

export const createEmailRequest = async ({ id }, cookies) =>
  getList(ENDPOINTS.DYNAMIC.USER_SUBSCRIPTIONS({ id, limit: 100 }), {}, cookies).then(response => response?.data);

const queries = {
  /* NOTE: START OF USER RELATED QUERIES
    All user related queries that requires refetch together on certain conditions like
    user profile update or subscription update should be added here and the
    query key name should start with _GET_USER_
  */

  // NOTE: refetched when "details" options is true in useRefetchUserQueries
  getUserDetails: getQuery('_GET_USER_DETAILS', fetchUserDetails),
  getUserProfiles: getQuery('_GET_USER_PROFILES', fetchUserProfiles),

  // NOTE: refetched when "permissions" options is true in useRefetchUserQueries
  getUserPermissions: getQuery('_GET_USER_PERMISSIONS', fetchUserPermissions),

  // NOTE: refetched when "subscriptions" options is true in useRefetchUserQueries
  getUserSubscriptions: getQuery('_GET_USER_SUBSCRIPTIONS', fetchUserSubscriptions),
  getTargetProfileSubscriptions: getQuery('_GET_USER_TARGET_PROFILE_SUBSCRIPTIONS', fetchTargetProfileSubscriptions),

  // NOTE: refetched when "payments" options is true in useRefetchUserQueries
  getUserPaymentMethods: getQuery('_GET_USER_PAYMENT_METHODS', fetchUserPaymentMethods),
  // NOTE: END OF USER RELATED QUERIES

  updateUserDetails: updateQuery(ENDPOINTS.DYNAMIC.USERS),
  updateActiveProfileId: updateQuery(ENDPOINTS.DYNAMIC.USER_ACTIVE_PROFILE),
  cancelSubscription: updateQuery(ENDPOINTS.STATIC.USERS_SUBSCRIPTIONS),
  updateUserSubscription: updateQuery(ENDPOINTS.STATIC.USERS_SUBSCRIPTIONS),
  getRegistration: ({ userId, registrationId, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'REGISTRATION', userId, registrationId],
    queryFn: async () =>
      getOne(ENDPOINTS.DYNAMIC.REGISTRATION(userId), registrationId, cookies).then(response => response.data),
    ...defaultConfig,
    ...queryConfig,
  }),
  getCurrentUserRequestAccess: ({ user_id, limit = 5, status, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'GET_CURRENT_USER_REQUEST_ACCESS', `${user_id}`, `${limit}`],
    queryFn: async ({ pageParam = 1 }) =>
      getCurrentUserRequestsAccess({ user_id, limit, page: pageParam, status }, cookies),
    ...queryConfig,
    ...defaultConfig,
  }),
  getCountries: (cookies, language) => ({
    queryKey: [getLanguage(language), 'COUNTRIES'],
    queryFn: async () => getWholeList(ENDPOINTS.STATIC.COUNTRIES_ENDPOINT, cookies).then(response => response.data),
    ...selectConfig,
    ...defaultConfig,
  }),
  getLanguages: (cookies, language) => ({
    queryKey: [getLanguage(language), 'LANGUAGES'],
    queryFn: async () => getWholeList(ENDPOINTS.STATIC.LANGUAGES_LIST, cookies).then(response => response.data),
    ...selectConfig,
    ...defaultConfig,
  }),
  getProfilePreferences: ({ profileTypeId, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'PROFILE_PREFERENCES', profileTypeId],
    queryFn: async () =>
      getOne(ENDPOINTS.STATIC.PROFILE_TYPE_PREFERENCES, profileTypeId, cookies).then(response => response.data),
    ...defaultConfig,
    ...queryConfig,
  }),
  getProfessions: (
    {
      hasParentProfession = false,
      userId,
      tags,
      tagType,
      querySearch,
      isSelect,
      limit,
      queryConfig = {},
      professionId,
    },
    cookies,
    language,
  ) => ({
    queryKey: [
      getLanguage(language),
      'PROFESSIONS',
      hasParentProfession,
      tags,
      tagType,
      isSelect,
      querySearch,
      professionId,
    ],
    queryFn: async () => {
      const query = {
        queryParams: {
          user_id: userId,
          tag: tags,
          tag_type: tagType,
          sort: SORT_OPTION_VALUES.PROFILE_COUNT,
          query: querySearch,
          limit: limit || 25,
          id: professionId || '',
          ...(hasParentProfession && {
            parent_profession: `${PARENT_PROFESSION_SLUG.SINGER},${PARENT_PROFESSION_SLUG.INSTRUMENTALIST}`,
          }),
          validation_status: 'pending,approved',
        },
      };

      return getList(ENDPOINTS.STATIC.PROFESSIONS, query, cookies).then(response => response.data);
    },
    ...defaultConfig,
    ...queryConfig,
    ...(isSelect && selectConfig),
  }),

  getProfessionSuggestions: (
    { userId, querySearch, tags, tagType, hasParentProfession, queryConfig = {} },
    cookies,
    language,
  ) => ({
    queryKey: [getLanguage(language), 'SUGGESTED_PROFESSIONS', querySearch, tags, tagType, hasParentProfession],
    queryFn: async () => {
      const query = {
        queryParams: {
          user_id: userId,
          query: querySearch,
          tag: tags,
          tag_type: tagType,
          ...(hasParentProfession && {
            parent_profession: `${PARENT_PROFESSION_SLUG.SINGER},${PARENT_PROFESSION_SLUG.INSTRUMENTALIST}`,
          }),
          validation_status: 'pending,approved',
        },
      };
      return getList(ENDPOINTS.STATIC.PROFESSIONS, query, cookies).then(response => response.data);
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  getOrganizations: ({ query = '', isOrg, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'ORGANIZATIONS', query, isOrg],
    queryFn: async () => {
      const url = isOrg ? ENDPOINTS.STATIC.ORGANIZATIONS : ENDPOINTS.STATIC.AGENCIES;
      const queryParams = { queryParams: { query, sort: isOrg ? '-production_count' : '' } };
      return getList(url, queryParams, cookies).then(response => response.data);
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  getProfile: ({ profileId, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'USER_PROFILES', profileId],
    queryFn: async () => getOne(ENDPOINTS.STATIC.PROFILES_ENDPOINT, profileId, cookies).then(response => response.data),
    ...defaultConfig,
    ...queryConfig,
  }),
  getRegistrations: ({ userId, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'GET_REGISTRATIONS', userId],
    queryFn: async () => {
      const param = { queryParams: { limit: 100 } };
      return getList(`users/${userId}/registrations`, param, cookies).then(response => response.data);
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  getProducts: ({ product_type = [], profile_type_id = '', queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'GET_PRODUCTS', profile_type_id, product_type],
    queryFn: async () => getProductsList(cookies, product_type, profile_type_id).then(response => response.data),
    ...defaultConfig,
    ...queryConfig,
  }),
  getSubscriptions: ({ profileId, agencyId, orgId, queryConfig = {}, query = {} }, cookies) => ({
    queryKey: [getLanguage(), 'GET_SUBSCRIPTIONS', profileId, agencyId, orgId],
    queryFn: async () => {
      const param = {
        queryParams: { profile_id: profileId, agency_id: agencyId, organization_id: orgId, ...query, limit: 100 },
      };
      return getList(ENDPOINTS.STATIC.USERS_SUBSCRIPTIONS, param, cookies).then(response => response.data);
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  searchProfile: ({ params, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'GET_PROFILES', JSON.stringify(params)],
    queryFn: async () => {
      const query = {
        queryParams: params,
      };
      return getList(ENDPOINTS.STATIC.PROFILES_ENDPOINT, query, cookies).then(response => response.data);
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  getReferral: ({ isPersonalReferral, token, email, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'GET_REFERRAL', isPersonalReferral, token, email],
    queryFn: async () => {
      const query = {
        queryParams: {
          token,
          email,
        },
      };
      const url = isPersonalReferral ? ENDPOINTS.STATIC.USER_REFERRALS_STATUS : ENDPOINTS.STATIC.USER_REFERRALS;
      return getList(url, query, cookies).then(response => response.data);
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  getIdentityVerification: ({ userId, queryConfig = {} }, cookies, language) => ({
    queryKey: [getLanguage(language), 'GET_IDENTITY_VERIFICATION', userId],
    queryFn: async () =>
      getList(ENDPOINTS.DYNAMIC.IDENTITY_VERIFICATION(userId), cookies).then(response => response.data),
    ...defaultConfig,
    ...queryConfig,
  }),
  getPerformanceTypes: ({ querySearch, queryConfig = {} }, cookies) => ({
    queryKey: [getLanguage(), 'ORGANIZATIONS_PERFORMANCE_TYPES', querySearch],
    queryFn: async () => {
      const params = { queryParams: { query: querySearch, validation_status_id: '1,2,3,4,5' } };
      return getList(ENDPOINTS.STATIC.PERFORMANCES_TYPES, params, cookies).then(response => response?.data);
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  getWorkTypes: ({ querySearch, queryConfig = {} }, cookies) => ({
    queryKey: [getLanguage(), 'WORK_TYPES', querySearch],
    queryFn: async () => {
      const params = { queryParams: { query: querySearch, validation_status: 'pending,approved', limit: 25 } };
      return getList(ENDPOINTS.STATIC.MUSICAL_WORK_TYPES_ENDPOINT, params, cookies).then(response => response?.data);
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  getOrgPerformanceTypes: ({ orgId, queryConfig = {} }, cookies) => ({
    queryKey: [getLanguage(), 'GET_ORGANIZATIONS_PERFORMANCE_TYPES', orgId],
    queryFn: async () =>
      getList(ENDPOINTS.DYNAMIC.ORGANIZATION_PERFORMANCE_TYPES(orgId), cookies).then(response => response?.data),
    ...defaultConfig,
    ...queryConfig,
  }),
  // TODO: FB-000000 Should be moved to containers/Organizations/queries.js
  getOrgWorkTypes: ({ orgId, queryConfig = {} }, cookies) => ({
    queryKey: [getLanguage(), 'GET_ORGANIZATIONS_WORK_TYPES', orgId],
    queryFn: async () => {
      const params = { queryParams: { limit: 50 } };
      return getList(ENDPOINTS.DYNAMIC.ORGANIZATION_WORK_TYPES(orgId), params, cookies).then(
        response => response?.data,
      );
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  getOrgStages: ({ orgId, queryConfig = {} }, cookies) => ({
    queryKey: [getLanguage(), 'GET_ORGANIZATIONS_STAGES', orgId],
    queryFn: async () => {
      const params = { queryParams: { limit: 50 } };
      return getList(ENDPOINTS.DYNAMIC.ORGANIZATION_STAGES(orgId), params, cookies).then(response => response?.data);
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  getProfileAccesses: ({ userId, profileId, queryConfig = {} }, cookies) => ({
    queryKey: [getLanguage(), '_GET_USER_PROFILE_ACCESSES', userId, profileId],
    queryFn: async () => {
      const params = { queryParams: { user_id: userId, profile_id: profileId, limit: 1000 } };
      return getList(ENDPOINTS.STATIC.PROFILE_ACCESSES, params, cookies).then(response => response?.data);
    },
    ...defaultConfig,
    ...queryConfig,
  }),
  getOrgDepartments: ({ query, queryConfig = {} }, cookies) => ({
    queryKey: [getLanguage(), 'GET_ORGANIZATIONS_DEPARTMENTS', query],
    queryFn: async () => {
      const params = { queryParams: { limit: 50, query } };
      return getList(ENDPOINTS.STATIC.ORGANIZATIONS_DEPARTMENTS, params, cookies).then(response => response?.data);
    },
    ...selectConfig,
    ...queryConfig,
  }),
  deleteProfileAccess: deleteQuery(({ profileAccessId }) => ENDPOINTS.DYNAMIC.DELETE_PROFILE_ACCESSES(profileAccessId)),
  createSubscription: postQuery(ENDPOINTS.STATIC.USERS_SUBSCRIPTIONS),
  getPreferences: getQuery('GET_PREFERENCES', fetchPreferences),
  getWorksTypes: getQuery('GET_WORKS_TYPES', fetchWorksTypes),
  getFingerprintDevices: getQuery('GET_FINGERPRINT_DEVICES', fetchFingerprintDevices),
};

const useCustomMutation = (mutationFn, options = {}) =>
  useMutation(mutationFn, {
    onSuccess: options?.onSuccess,
    onError: options?.onError,
    onSettled: options?.onSettled,
  });

export const useLoginMutation = (options = {}) => {
  const mutationFn = payload =>
    API.login({
      email: payload.email,
      password: payload.password,
      otp: payload.otp,
      isTrustedDevice: payload.isTrustedDevice,
      popupSource: payload?.popupSource,
    });
  return useCustomMutation(mutationFn, options);
};

export const useCreateUserMutation = (options = {}) => {
  const mutationFn = payload =>
    API.registerUser({
      email: payload.email,
      firstName: payload.firstName,
      lastName: payload.lastName,
      password: null,
      preferredLanguage: 'en',
    });
  return useCustomMutation(mutationFn, options);
};

export const useCreateRegistrationMutation = (options = {}) => {
  const mutationFn = payload => API.createRegistration(payload, payload.entities.user.id);
  return useCustomMutation(mutationFn, options);
};

export const useResendOtpMutation = (options = {}) => {
  const mutationFn = payload => API.requestValidateEmailOTP(payload);
  return useCustomMutation(mutationFn, options);
};

export const useVerifyOtpMutation = (options = {}) => {
  const mutationFn = payload => API.validateEmailOTP(payload, payload);
  return useCustomMutation(mutationFn, options);
};

export const useUpdateRegistrationMutation = (options = {}) => {
  const mutationFn = payload =>
    updateOne(ENDPOINTS.DYNAMIC.REGISTRATION(payload?.entities?.user?.id), payload?.id, payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreateProfileMutation = (options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.STATIC.PROFILES, payload);
  return useCustomMutation(mutationFn, options);
};

export const useResetUserPasswordMutation = (options = {}) => {
  const mutationFn = email => API.resetUserPassword(email);
  return useCustomMutation(mutationFn, options);
};

export const useCreateProfilePreferencesMutation = (options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.DYNAMIC.PROFILE_PREFERENCES(payload?.profileId), payload?.preferences);
  return useCustomMutation(mutationFn, options);
};

export const useCreateProfessionMutation = (options = {}) => {
  const mutationFn = payload => API.create(ENDPOINTS.STATIC.PROFESSIONS, payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreateOrganizationMutation = (isAOSProfile, options = {}) => {
  const url = isAOSProfile ? ENDPOINTS.STATIC.ORGANIZATIONS : ENDPOINTS.STATIC.AGENCIES;
  const mutationFn = payload => create(url, payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreateOrganizationContactsMutation = (options = {}) => {
  const mutationFn = payload => createOrganizationContacts(payload?.orgId, payload?.contacts);
  return useCustomMutation(mutationFn, options);
};

export const useUpdateProfilePhoto = (profileId, options = {}) => {
  const mutationFn = payload => {
    const { params, files } = payload;
    return API.updateOne(ENDPOINTS.DYNAMIC.PROFILE(profileId), null, params, files);
  };

  return useMutation(mutationFn, options);
};

export const useAddPerformance = (userId, options = {}) => {
  const mutationFn = payload => API.create(ENDPOINTS.DYNAMIC.USER_PERFORMACES(userId), payload);
  return useMutation(mutationFn, options);
};

export const useUpdatePerformance = (userId, options = {}) => {
  const mutationFn = payload => API.updateOne(ENDPOINTS.DYNAMIC.USER_PERFORMACES(userId), payload?.id, payload);
  return useMutation(mutationFn, options);
};

export const useUpdateProfileMutation = (options = {}) => {
  const mutationFn = payload => updateOne(ENDPOINTS.STATIC.PROFILES, payload?.profileId, payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreateIdentityVerificationMutation = (userId, options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.DYNAMIC.IDENTITY_VERIFICATION(userId), payload);
  return useCustomMutation(mutationFn, options);
};

export const useUpdateUserMutation = (userId, options = {}) => {
  const mutationFn = payload => updateOne(ENDPOINTS.STATIC.ACCOUNT_CREATE, userId, payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreateProfileAccessMutation = (options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.DYNAMIC.SET_PROFILE_ACCESS_REQUESTS(payload?.profile?.id), payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreateReferralStatusMutation = (options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.DYNAMIC.GIFT_SUBSCRIPTIONS(payload?.sender?.user?.id), payload);
  return useCustomMutation(mutationFn, options);
};

export const useUpdateReferralStatusMutation = (options = {}) => {
  const mutationFn = payload =>
    updateOne(ENDPOINTS.DYNAMIC.GIFT_SUBSCRIPTIONS(payload?.senderId), payload?.referralId, payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreateOrgPerformanceTypesMutation = (orgId, options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.DYNAMIC.ORGANIZATION_PERFORMANCE_TYPES(orgId), payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreatePerformanceTypeMutation = (options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.STATIC.PERFORMANCES_TYPES, payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreateWorkTypeTypeMutation = (options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.STATIC.MUSICAL_WORK_TYPES_ENDPOINT, payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreateOrgWorkTypesMutation = (orgId, options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.DYNAMIC.ORGANIZATION_WORK_TYPES(orgId), payload);
  return useCustomMutation(mutationFn, options);
};

export const useCreateAdditionalEmail = (userId, options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.DYNAMIC.CREATE_ADDITIONAL_EMAIL(userId), payload);
  return useCustomMutation(mutationFn, options);
};

export const useValidateAdditionalEmail = (userId, options = {}) => {
  const mutationFn = payload => create(ENDPOINTS.DYNAMIC.VALIDATE_ADDITIONAL_EMAIL(userId), payload);
  return useCustomMutation(mutationFn, options);
};

export default queries;
